import { apiPostV4, apiV4BaseUrl } from "@/services/api/httpClient";
import localCommandContract from "@/agent/v4CommandRoutes.json";
import type { AgentMapContext } from "@/services/api/agentApi";
import type {
  AgentV2ChatResponse,
  CommandReceiptRequest,
  CommandResultResponse,
  V4ProcessStep
} from "@/agent/types";

export interface AgentV4ChatResponse extends AgentV2ChatResponse {
  followUpQuestions?: string[];
  routeDraft?: Record<string, unknown> | null;
  actionPlan?: Record<string, unknown> | null;
  goalAssessment?: Record<string, unknown> | null;
  taskFrame?: Record<string, unknown> | null;
  entityResolution?: Record<string, unknown> | null;
  processSteps?: V4ProcessStep[];
  actionExecutionTrace?: Record<string, unknown> | null;
  goalPlan?: Record<string, unknown> | null;
  domainState?: Record<string, unknown> | null;
}

export interface AgentV4CommandResultResponse extends CommandResultResponse {
  actionPlan?: Record<string, unknown> | null;
  goalAssessment?: Record<string, unknown> | null;
  observedState?: Record<string, unknown> | null;
  processSteps?: V4ProcessStep[];
  actionExecutionTrace?: Record<string, unknown> | null;
  goalPlan?: Record<string, unknown> | null;
  domainState?: Record<string, unknown> | null;
}

/** Reject a mixed frontend/backend deployment before dispatching page actions. */
export async function assertAgentV4CapabilityContract(signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${apiV4BaseUrl}/agent/capabilities`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal
  });
  if (!response.ok) throw new Error(`江小忆能力契约读取失败（${response.status}）`);
  const payload = await response.json() as {
    data?: { version?: string; commandRoutes?: Record<string, string> };
  };
  const server = payload.data;
  const localRoutes: Record<string, string> = localCommandContract.commandRoutes;
  const remoteRoutes = server?.commandRoutes;
  const sameRoutes = remoteRoutes && Object.keys(localRoutes).length === Object.keys(remoteRoutes).length
    && Object.entries(localRoutes).every(([type, route]) => remoteRoutes[type] === route);
  if (server?.version !== localCommandContract.version || !sameRoutes) {
    throw new Error("江小忆前后端能力版本不一致，请刷新页面或更新服务后重试。");
  }
}

export function sendAgentV4Message(
  payload: { sessionId?: string; message: string; context: AgentMapContext },
  signal?: AbortSignal
): Promise<AgentV4ChatResponse> {
  return apiPostV4<AgentV4ChatResponse>("/agent/chat", payload, signal).then(mapV4ChatResponse);
}

/** Stream actual V4 LangGraph node transitions; no synthetic task list is emitted. */
export async function sendAgentV4MessageStream(
  payload: { sessionId?: string; message: string; context: AgentMapContext },
  onStep: (step: V4ProcessStep) => void,
  signal?: AbortSignal
): Promise<AgentV4ChatResponse> {
  const response = await fetch(`${apiV4BaseUrl}/agent/chat/stream`, {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
    signal
  });
  if (!response.ok) throw new Error(`V4 内容服务请求失败（${response.status}）`);
  if (!response.body) throw new Error("浏览器不支持 V4 流式读取。");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let settled = false;
  let resolveResult!: (value: AgentV4ChatResponse) => void;
  let rejectResult!: (reason: unknown) => void;
  const resultPromise = new Promise<AgentV4ChatResponse>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const dispatch = (raw: string): void => {
    if (!raw.trim()) return;
    let eventType = "message";
    let data = "";
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) eventType = line.slice(6).trim();
      else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trimStart();
    }
    if (!data) return;
    let parsed: any;
    try { parsed = JSON.parse(data); } catch { return; }
    if (eventType === "step") {
      if (parsed && typeof parsed.id === "string") onStep(parsed as V4ProcessStep);
    } else if (eventType === "result") {
      settled = true;
      resolveResult(mapV4ChatResponse(parsed as AgentV4ChatResponse));
    } else if (eventType === "error") {
      settled = true;
      rejectResult(new Error(parsed?.message || "V4 回复生成失败。"));
    }
  };

  const pump = (): Promise<void> => reader.read().then(({ done, value }) => {
    if (done) {
      if (!settled) rejectResult(new Error("V4 流式连接中断：未收到完整回复。"));
      return;
    }
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      dispatch(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf("\n\n");
    }
    if (!settled) return pump();
  });
  void pump().catch(rejectResult);
  return resultPromise;
}

function mapV4ChatResponse(response: AgentV4ChatResponse): AgentV4ChatResponse {
  return {
    sessionId: response.sessionId,
    turnId: response.turnId,
    planId: response.planId,
    status: response.status,
    answer: response.answer || "",
    cards: response.cards || [],
    sources: [],
    uiCommands: response.uiCommands || [],
    suggestedActions: response.followUpQuestions || response.suggestedActions || [],
    debugToolSteps: [],
    model: "v4-shadow",
    actionPlan: response.actionPlan,
    goalAssessment: response.goalAssessment,
    taskFrame: response.taskFrame,
    entityResolution: response.entityResolution,
    v4ProcessSteps: response.processSteps || [],
    actionExecutionTrace: response.actionExecutionTrace || null,
    goalPlan: response.goalPlan || null,
    domainState: response.domainState || null
  };
}

export function reportAgentV4CommandResult(
  payload: CommandReceiptRequest,
  signal?: AbortSignal
): Promise<AgentV4CommandResultResponse> {
  return apiPostV4<AgentV4CommandResultResponse>("/agent/commands/result", payload, signal);
}

export function fetchAgentV4FinalAnswer(
  payload: { sessionId?: string; turnId?: string; planId?: string },
  signal?: AbortSignal
): Promise<{ answer: string; planStatus: string; goalAssessment?: Record<string, unknown>; suggestedActions?: string[]; followUpQuestions?: string[]; processSteps?: V4ProcessStep[]; actionExecutionTrace?: Record<string, unknown> | null }> {
  return apiPostV4<{ answer: string; planStatus: string; goalAssessment?: Record<string, unknown>; suggestedActions?: string[]; followUpQuestions?: string[]; processSteps?: V4ProcessStep[]; actionExecutionTrace?: Record<string, unknown> | null }>(
    "/agent/answer", payload, signal);
}
